import React from 'react';
import styled from 'styled-components';
import { EditorState, EditorTool, EditorMode, MIN_ZOOM, MAX_ZOOM } from '../../types/mapEditor';

const ToolbarContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: #1a1a2e;
  border-bottom: 1px solid #333;
  flex-wrap: wrap;
`;

const ToolGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0 8px;
  border-right: 1px solid #333;

  &:last-child {
    border-right: none;
  }
`;

const ToolButton = styled.button<{ $active?: boolean; $disabled?: boolean; $danger?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 1px solid ${props => props.$active ? '#4CAF50' : props.$danger ? '#f44336' : '#444'};
  border-radius: 4px;
  background: ${props => props.$active ? '#4CAF50' : props.$danger ? '#f44336' : '#2a2a3e'};
  color: ${props => props.$disabled ? '#666' : '#fff'};
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.$disabled ? 0.5 : 1};
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: ${props => props.$active ? '#45a049' : props.$danger ? '#d32f2f' : '#3a3a4e'};
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const ColorInput = styled.input`
  width: 36px;
  height: 36px;
  border: 1px solid #444;
  border-radius: 4px;
  background: #2a2a3e;
  cursor: pointer;
  padding: 2px;

  &::-webkit-color-swatch-wrapper {
    padding: 2px;
  }

  &::-webkit-color-swatch {
    border: none;
    border-radius: 2px;
  }
`;

const ZoomControl = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;

  span {
    min-width: 50px;
    text-align: center;
    color: #fff;
    font-size: 12px;
  }
`;

const Label = styled.span`
  font-size: 12px;
  color: #FFFFFF;
  font-weight: 500;
  margin-right: 4px;
`;

const StrokeWidthSelect = styled.select`
  padding: 4px 8px;
  background: #2a2a3e;
  border: 1px solid #444;
  border-radius: 4px;
  color: #fff;
  font-size: 12px;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #4CAF50;
  }
`;

const SelectedCount = styled.span`
  font-size: 11px;
  color: #4CAF50;
  padding: 4px 8px;
  background: rgba(76, 175, 80, 0.1);
  border-radius: 4px;
`;

interface EditorToolbarProps {
  editorState: EditorState;
  mode: EditorMode;
  isReadOnly: boolean;
  onToolChange: (tool: EditorTool) => void;
  onZoomChange: (zoom: number) => void;
  onResetView?: () => void;
  onSave: () => void;
  onUndo: () => void;
  onRedo: () => void;
  onExport: () => void;
  onColorChange: (fill: string, stroke: string) => void;
  onStrokeWidthChange: (width: number) => void;
  canUndo: boolean;
  canRedo: boolean;
  selectedCount: number;
  onDeleteSelected: () => void;
  onCopy: () => void;
  onPaste: () => void;
  hasClipboard: boolean;
  canvasBgColor?: 'dark' | 'white';
  onToggleCanvasBg?: () => void;
}

const EditorToolbar: React.FC<EditorToolbarProps> = ({
  editorState,
  mode,
  isReadOnly,
  onToolChange,
  onZoomChange,
  onResetView,
  onSave,
  onUndo,
  onRedo,
  onExport,
  onColorChange,
  onStrokeWidthChange,
  canUndo,
  canRedo,
  selectedCount,
  onDeleteSelected,
  onCopy,
  onPaste,
  hasClipboard,
  canvasBgColor,
  onToggleCanvasBg,
}) => {
  return (
    <ToolbarContainer>
      {/* Drawing Tools - only in schema mode */}
      {mode === 'schema' && !isReadOnly && (
        <ToolGroup>
          <Label>Tools:</Label>
          <ToolButton
            $active={editorState.tool === 'select'}
            onClick={() => onToolChange('select')}
            title="Select (V)"
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M7 2l10 10-4 1 2 5-2 1-2-5-4 3z" />
            </svg>
          </ToolButton>
          <ToolButton
            $active={editorState.tool === 'line'}
            onClick={() => onToolChange('line')}
            title="Line (L)"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="5" y1="19" x2="19" y2="5" />
            </svg>
          </ToolButton>
          <ToolButton
            $active={editorState.tool === 'text'}
            onClick={() => onToolChange('text')}
            title="Text (T)"
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M5 4v3h5.5v12h3V7H19V4H5z" />
            </svg>
          </ToolButton>
          <ToolButton
            $active={editorState.tool === 'eraser'}
            onClick={() => onToolChange('eraser')}
            title="Eraser (E)"
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M15.14 3c-.51 0-1.02.2-1.41.59L2.59 14.73c-.78.77-.78 2.04 0 2.81L5.03 20H12l9.41-9.41c.78-.78.78-2.05 0-2.83l-4.86-4.17c-.39-.39-.9-.59-1.41-.59zm-2.83 11.17L7.17 9l6-6 5.14 5.14-6 6z" />
            </svg>
          </ToolButton>
          <ToolButton
            $active={editorState.tool === 'border'}
            onClick={() => onToolChange('border')}
            title="Map Border (B)"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeDasharray="3 2">
              <rect x="3" y="3" width="18" height="18" rx="1" />
            </svg>
          </ToolButton>
        </ToolGroup>
      )}

      {/* Colors & Stroke - only in schema mode */}
      {mode === 'schema' && !isReadOnly && (
        <ToolGroup>
          <Label>Stroke:</Label>
          <ColorInput
            type="color"
            value={editorState.strokeColor}
            onChange={(e) => onColorChange(editorState.fillColor, e.target.value)}
            title="Stroke Color"
          />
          <Label>Width:</Label>
          <StrokeWidthSelect
            value={editorState.strokeWidth}
            onChange={(e) => onStrokeWidthChange(Number(e.target.value))}
            title="Stroke Width"
          >
            <option value={1}>1px</option>
            <option value={2}>2px</option>
            <option value={3}>3px</option>
            <option value={4}>4px</option>
            <option value={5}>5px</option>
            <option value={8}>8px</option>
            <option value={10}>10px</option>
          </StrokeWidthSelect>
        </ToolGroup>
      )}

      {/* Selection actions - only when something is selected */}
      {mode === 'schema' && !isReadOnly && selectedCount > 0 && (
        <ToolGroup>
          <SelectedCount>{selectedCount} selected</SelectedCount>
          <ToolButton
            onClick={onCopy}
            title="Copy (Ctrl+C)"
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" />
            </svg>
          </ToolButton>
          <ToolButton
            $danger
            onClick={onDeleteSelected}
            title="Delete (Delete)"
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
            </svg>
          </ToolButton>
        </ToolGroup>
      )}

      {/* Clipboard actions */}
      {mode === 'schema' && !isReadOnly && hasClipboard && selectedCount === 0 && (
        <ToolGroup>
          <ToolButton
            onClick={onPaste}
            title="Paste (Ctrl+V)"
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 2h-4.18C14.4.84 13.3 0 12 0c-1.3 0-2.4.84-2.82 2H5c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-7 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm7 18H5V4h2v3h10V4h2v16z" />
            </svg>
          </ToolButton>
          <Label style={{ marginLeft: 4 }}>Paste available</Label>
        </ToolGroup>
      )}

      {/* History */}
      {!isReadOnly && (
        <ToolGroup>
          <ToolButton
            onClick={onUndo}
            $disabled={!canUndo}
            title="Undo (Ctrl+Z)"
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12.5 8c-2.65 0-5.05.99-6.9 2.6L2 7v9h9l-3.62-3.62c1.39-1.16 3.16-1.88 5.12-1.88 3.54 0 6.55 2.31 7.6 5.5l2.37-.78C21.08 11.03 17.15 8 12.5 8z" />
            </svg>
          </ToolButton>
          <ToolButton
            onClick={onRedo}
            $disabled={!canRedo}
            title="Redo (Ctrl+Shift+Z)"
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.4 10.6C16.55 8.99 14.15 8 11.5 8c-4.65 0-8.58 3.03-9.96 7.22L3.9 16c1.05-3.19 4.05-5.5 7.6-5.5 1.95 0 3.73.72 5.12 1.88L13 16h9V7l-3.6 3.6z" />
            </svg>
          </ToolButton>
        </ToolGroup>
      )}

      {/* Zoom */}
      <ToolGroup>
        <Label>Zoom:</Label>
        <ZoomControl>
          <ToolButton
            onClick={() => onZoomChange(editorState.zoom - 0.1)}
            $disabled={editorState.zoom <= MIN_ZOOM}
            title="Zoom Out"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          </ToolButton>
          <span>{Math.round(editorState.zoom * 100)}%</span>
          <ToolButton
            onClick={() => onZoomChange(editorState.zoom + 0.1)}
            $disabled={editorState.zoom >= MAX_ZOOM}
            title="Zoom In"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          </ToolButton>
          {onResetView && (
            <ToolButton
              onClick={onResetView}
              title="Reset View (100% & Center)"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="3" />
                <path d="M12 2v4M12 18v4M2 12h4M18 12h4" />
              </svg>
            </ToolButton>
          )}
        </ZoomControl>
      </ToolGroup>

      {/* View Options */}
      <ToolGroup>
        {onToggleCanvasBg && (
          <ToolButton
            $active={canvasBgColor === 'white'}
            onClick={onToggleCanvasBg}
            title={`Background: ${canvasBgColor === 'white' ? 'White' : 'Dark'}`}
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              {canvasBgColor === 'white' ? (
                <path d="M12 7c-2.76 0-5 2.24-5 5s2.24 5 5 5 5-2.24 5-5-2.24-5-5-5zM2 13h2c.55 0 1-.45 1-1s-.45-1-1-1H2c-.55 0-1 .45-1 1s.45 1 1 1zm18 0h2c.55 0 1-.45 1-1s-.45-1-1-1h-2c-.55 0-1 .45-1 1s.45 1 1 1zM11 2v2c0 .55.45 1 1 1s1-.45 1-1V2c0-.55-.45-1-1-1s-1 .45-1 1zm0 18v2c0 .55.45 1 1 1s1-.45 1-1v-2c0-.55-.45-1-1-1s-1 .45-1 1zM5.99 4.58c-.39-.39-1.03-.39-1.41 0-.39.39-.39 1.03 0 1.41l1.06 1.06c.39.39 1.03.39 1.41 0s.39-1.03 0-1.41L5.99 4.58zm12.37 12.37c-.39-.39-1.03-.39-1.41 0-.39.39-.39 1.03 0 1.41l1.06 1.06c.39.39 1.03.39 1.41 0 .39-.39.39-1.03 0-1.41l-1.06-1.06zm1.06-10.96c.39-.39.39-1.03 0-1.41-.39-.39-1.03-.39-1.41 0l-1.06 1.06c-.39.39-.39 1.03 0 1.41s1.03.39 1.41 0l1.06-1.06zM7.05 18.36c.39-.39.39-1.03 0-1.41-.39-.39-1.03-.39-1.41 0l-1.06 1.06c-.39.39-.39 1.03 0 1.41s1.03.39 1.41 0l1.06-1.06z" />
              ) : (
                <path d="M12 3c-4.97 0-9 4.03-9 9s4.03 9 9 9 9-4.03 9-9c0-.46-.04-.92-.1-1.36-.98 1.37-2.58 2.26-4.4 2.26-2.98 0-5.4-2.42-5.4-5.4 0-1.81.89-3.42 2.26-4.4-.44-.06-.9-.1-1.36-.1z" />
              )}
            </svg>
          </ToolButton>
        )}
      </ToolGroup>

      {/* Actions */}
      <ToolGroup>
        {!isReadOnly && (
          <ToolButton
            onClick={onSave}
            title="Save (Ctrl+S)"
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M17 3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V7l-4-4zm-5 16c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3zm3-10H5V5h10v4z" />
            </svg>
          </ToolButton>
        )}
      </ToolGroup>
    </ToolbarContainer>
  );
};

export default EditorToolbar;
